import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Clock, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { catalog, catalogLabels } from "./catalogContent";

const HERO_IMG =
  "https://images.unsplash.com/photo-1516815231560-8f41ec531527?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const PriceRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-[#EAE6DF] last:border-0">
    <span className="text-xs uppercase tracking-[0.2em] text-[#4A5568]">{label}</span>
    <span className="font-serif text-lg text-[#1A1F24]">${value}</span>
  </div>
);

const CatalogCard = ({ item, labels, onBook, index }) => {
  const isEven = index % 2 === 0;
  return (
    <article
      data-testid={`catalog-item-${item.id}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center py-16 border-b border-[#EAE6DF]`}
    >
      <div className={`lg:col-span-6 ${isEven ? "" : "lg:order-2"}`}>
        <div className="aspect-[4/3] overflow-hidden bg-[#EAE6DF]">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
          />
        </div>
      </div>

      <div className={`lg:col-span-6 ${isEven ? "" : "lg:order-1"}`}>
        <p className="text-[#2E4F4F] text-xs uppercase tracking-[0.3em] mb-3">
          {item.category}
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1F24] tracking-tight mb-2">
          {item.name}
        </h2>
        <p className="text-[#C6A87C] italic mb-6">{item.tagline}</p>

        <p className="text-[#4A5568] leading-relaxed mb-6">{item.description}</p>

        <div className="flex items-center gap-2 text-sm text-[#4A5568] mb-6">
          <Clock className="w-4 h-4 text-[#2E4F4F]" />
          <span className="uppercase tracking-[0.15em] text-xs">
            {labels.duration}: {item.duration}
          </span>
        </div>

        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1A1F24] font-semibold mb-3">
            {labels.includes}
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {item.includes.map((inc) => (
              <li key={inc} className="flex items-start gap-2 text-sm text-[#4A5568]">
                <Check className="w-4 h-4 text-[#2E4F4F] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{inc}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#F9F6F0] border border-[#EAE6DF] p-5 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-3">
            {labels.from} · {labels.perVehicle}
          </p>
          <PriceRow label={labels.suv} value={item.prices.suv} />
          <PriceRow label={labels.van} value={item.prices.van} />
          <PriceRow label={labels.luxury} value={item.prices.luxury} />
        </div>

        <button
          data-testid={`book-${item.id}`}
          onClick={() => onBook(item)}
          className="group inline-flex items-center gap-3 bg-[#2E4F4F] hover:bg-[#1F3636] text-white px-8 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
        >
          {labels.bookNow}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </article>
  );
};

const CatalogPage = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const labels = catalogLabels[lang];
  const items = catalog[lang];

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category))),
    [items]
  );

  const [filter, setFilter] = useState("all");

  // Reset filter to "all" when language changes so the previously-selected
  // translated category label doesn't become stale and hide every item.
  useEffect(() => {
    setFilter("all");
  }, [lang]);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  const handleBook = () => {
    navigate("/#booking");
  };

  return (
    <div data-testid="catalog-page" className="bg-[#F9F6F0] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#F9F6F0]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12">
          <Link
            to="/"
            data-testid="catalog-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {labels.backHome}
          </Link>
          <p className="text-[#C6A87C] text-xs uppercase tracking-[0.3em] mb-4">
            {labels.eyebrow}
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight max-w-3xl leading-[1.05]">
            {labels.title}
          </h1>
          <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">{labels.subtitle}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#EAE6DF] bg-[#F9F6F0] sticky top-20 z-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex flex-wrap gap-2">
          <button
            key="all"
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors border ${
              filter === "all"
                ? "bg-[#1A1F24] text-white border-[#1A1F24]"
                : "bg-transparent text-[#1A1F24] border-[#1A1F24]/20 hover:border-[#1A1F24]"
            }`}
          >
            {labels.filterAll}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat}`}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors border ${
                filter === cat
                  ? "bg-[#1A1F24] text-white border-[#1A1F24]"
                  : "bg-transparent text-[#1A1F24] border-[#1A1F24]/20 hover:border-[#1A1F24]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Catalog list */}
      <main className="max-w-7xl mx-auto px-6 sm:px-12">
        {filtered.map((item, i) => (
          <CatalogCard key={item.id} item={item} labels={labels} onBook={handleBook} index={i} />
        ))}
      </main>

      <Footer />
    </div>
  );
};

export default CatalogPage;
